import { Controller } from "@hotwired/stimulus";
import { BranchOutcome } from "../Shared/Generated/BranchOutcome";
import { reportBranchOutcome, takeBranchArrival } from "./BranchOutcomeTracking";
import { boundedDuration, captureBrowserDiagnostics, newBranchDiagnostics } from "./BranchDiagnostics";
import { BranchFailurePhase } from "../Shared/Generated/BranchFailurePhase";

// Slugs/culture may change in the language-neutral redirect. Content identities must match.
function contentIdentity(path: string): string[] | null {
    const parts = path.toLowerCase().split("/").filter(Boolean);
    if (parts[0] !== "destinations") parts.shift();
    const id = (value?: string) => typeof value === "string" && /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/.test(value);
    const poiId = (value?: string) => typeof value === "string" && value.split("_").length <= 2 && value.split("_").every(id);
    if (parts[0] !== "destinations" || !id(parts[1])) return null;
    if (parts.length === 3) return ["destination", parts[1], parts[1]];
    if (parts.length === 6 && parts[3] === "places" && poiId(parts[4])) return ["poi", parts[1], parts[4]];
    if (parts[3] !== "tours" || !id(parts[4])) return null;
    if (parts.length === 6) return ["tour", parts[1], parts[4]];
    if (parts.length === 9 && parts[6] === "places" && poiId(parts[7])) return ["tourpoi", parts[1], parts[7], parts[4]];
    return null;
}

export default class BranchOutcomeArrivalController extends Controller<HTMLElement> {
    connect(): void {
        const pending = takeBranchArrival();
        if (!pending) return;
        const intended = contentIdentity(pending.path), current = contentIdentity(location.pathname);
        if (!intended || !current || JSON.stringify(intended) !== JSON.stringify(current)) return;
        const rendered = [this.element.dataset.seoPageType, this.element.dataset.seoDestinationId?.toLowerCase(), this.element.dataset.seoEntityId?.toLowerCase()];
        const failed = !!this.element.querySelector("[data-branch-content-error]");
        if (!failed && (!this.element.querySelector("[data-smartguide-app-page] h1") ||
            JSON.stringify(rendered) !== JSON.stringify(intended.slice(0, 3)))) return;
        const diagnostics = pending.requestDiagnostics ? { ...newBranchDiagnostics(), ...pending.requestDiagnostics } : undefined;
        if (diagnostics) {
            diagnostics.phase = BranchFailurePhase.Navigation;
            diagnostics.elapsedMs = boundedDuration(diagnostics.elapsedMs + Math.max(0, Date.now() - pending.createdAt));
            diagnostics.resolveHttpStatus = diagnostics.resolveRequests?.at(-1)?.httpStatus ?? null;
            try { captureBrowserDiagnostics(diagnostics); } catch { /* Optional diagnostics. */ }
        }
        void reportBranchOutcome({ attemptId: pending.attemptId, link: pending.link, correlationId: pending.correlationId,
            outcome: failed ? BranchOutcome.Failed : BranchOutcome.Opened, reason: failed ? "NavigationFailed" : null, diagnostics });
    }
}
